import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { apiGet, getSelectedOrgId, type Organization, type Service, type Trace, type TraceEvent } from "../api";
import { useGetTraceDetailQuery } from "../store/traces";
import { useServiceSlugMap } from "../store/services";
import { connectSocket, joinTracesStream } from "../socket";
import type { Channel, Socket } from "phoenix";
import { Icons } from "../components/icons";
import { JsonViewer } from "../components/JsonViewer";

interface TraceListResponse {
  traces: Trace[];
  next_cursor: string | null;
  total: number;
}

interface HistogramBucket {
  start: string;
  ok: number;
  slow: number;
  err: number;
}

type BucketSize = "1m" | "5m" | "1h";

function pickBucket(windowSec: number): { id: BucketSize; ms: number } {
  if (windowSec <= 60 * 60) return { id: "1m", ms: 60_000 };
  if (windowSec <= 6 * 60 * 60) return { id: "5m", ms: 5 * 60_000 };
  return { id: "1h", ms: 60 * 60_000 };
}

// El tipo TraceDetail vive ahora en store/traces.ts y se accede via el hook
// useGetTraceDetailQuery. Lo dejamos comentado por referencia histórica:
//   interface TraceDetail { trace: Trace; events: TraceEvent[] }

const STATE_OPTIONS = [
  { value: "ok",          label: "Éxito",   color: "var(--st-ok)" },
  { value: "slow",        label: "Lentas",  color: "var(--st-degraded)" },
  { value: "error",       label: "Error",   color: "var(--st-major)" },
  { value: "timeout",     label: "Timeout", color: "var(--st-partial)" },
] as const;

const RANGES = [
  { id: "15m", label: "15m", seconds: 15 * 60 },
  { id: "1h",  label: "1h",  seconds: 60 * 60 },
  { id: "6h",  label: "6h",  seconds: 6 * 60 * 60 },
  { id: "24h", label: "24h", seconds: 24 * 60 * 60 },
  { id: "7d",  label: "7d",  seconds: 7 * 24 * 60 * 60 },
  { id: "30d", label: "30d", seconds: 30 * 24 * 60 * 60 },
] as const;

type RangeId = (typeof RANGES)[number]["id"] | "custom";

// Entrada del filtro de servicios. `value` es lo que viaja en el query param:
// slug en vista de org, uuid en vista global (donde el slug es ambiguo porque
// orgs distintas — ej. sandbox y prod de NL — comparten slug).
interface ServiceFilterEntry {
  value: string;
  label: string;
  orgName?: string;
}

// Ancho del sidebar de filtros, ajustable arrastrando el borde derecho.
// El mínimo es el ancho histórico (--filters-w de tokens.css); el default
// arranca un poco más ancho para que los chips de org no se corten.
const FILTERS_W_MIN = 296;
const FILTERS_W_MAX = 560;
const FILTERS_W_DEFAULT = 340;

function clampFiltersW(w: number): number {
  return Math.min(FILTERS_W_MAX, Math.max(FILTERS_W_MIN, Math.round(w)));
}

export default function Traces() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  // -------- URL params = source of truth --------
  const range = (params.get("range") as RangeId) || "1h";
  const fromUrl = params.get("from") || "";
  const toUrl = params.get("to") || "";
  const stateCsv = params.get("state") || "ok,slow,error,timeout";
  const serviceCsv = params.get("service_ids") || "";
  const qUrl = params.get("q") || "";
  const govScope = params.get("org_id");

  const activeStates = useMemo(
    () => new Set(stateCsv.split(",").filter(Boolean)),
    [stateCsv]
  );
  const activeServices = useMemo(
    () => new Set(serviceCsv.split(",").filter(Boolean)),
    [serviceCsv]
  );

  // -------- Data --------
  const [traces, setTraces] = useState<Trace[]>([]);
  // Paginación keyset: el backend devuelve next_cursor mientras haya más
  // páginas y total con el count real del rango filtrado.
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [histogram, setHistogram] = useState<HistogramBucket[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [orgsMap, setOrgsMap] = useState<Record<string, Organization>>({});
  // Antes guardábamos el TraceDetail completo y abríamos el panel CUANDO terminaba
  // el fetch — eso causaba que el click no diera feedback inmediato. Ahora guardamos
  // solo el correlation_id, abrimos el panel al toque, y RTK Query trae el detalle
  // con skeleton mientras carga.
  const [selectedCid, setSelectedCid] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [showCustom, setShowCustom] = useState(range === "custom");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("traces_filters_collapsed") === "1")
  );

  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      localStorage.setItem("traces_filters_collapsed", next ? "1" : "0");
      return next;
    });
  };

  // -------- Ancho del sidebar de filtros (drag en el borde derecho) --------
  const [filtersW, setFiltersW] = useState(() => {
    if (typeof window === "undefined") return FILTERS_W_DEFAULT;
    const saved = Number(localStorage.getItem("traces_filters_w"));
    return Number.isFinite(saved) && saved >= FILTERS_W_MIN && saved <= FILTERS_W_MAX
      ? saved
      : FILTERS_W_DEFAULT;
  });
  const resizeRef = useRef<{ startX: number; startW: number } | null>(null);

  // Effective time window (always materialised as ISO timestamps, even for relative ranges)
  const timeWindow = useMemo(() => {
    if (range === "custom") {
      return { from: fromUrl || null, to: toUrl || null };
    }
    const r = RANGES.find((x) => x.id === range);
    if (!r) return { from: null, to: null };
    const now = new Date();
    const from = new Date(now.getTime() - r.seconds * 1000);
    return { from: from.toISOString(), to: now.toISOString() };
  }, [range, fromUrl, toUrl]);

  // Compute backend query params from URL state
  const apiParams = useMemo(() => {
    const sp = new URLSearchParams();
    sp.set("limit", "50");

    if (timeWindow.from) sp.set("from", timeWindow.from);
    if (timeWindow.to) sp.set("to", timeWindow.to);

    // State filter — only send if not the full set
    if (activeStates.size > 0 && activeStates.size < STATE_OPTIONS.length) {
      const expanded = [...activeStates]
        .flatMap((s) => (s === "slow" ? ["slow", "in_progress"] : [s]))
        .join(",");
      sp.set("state", expanded);
    }

    if (activeServices.size > 0) sp.set("service_ids", [...activeServices].join(","));
    if (qUrl) sp.set("q", qUrl);

    return sp.toString();
  }, [timeWindow, activeStates, activeServices, qUrl]);

  // -------- Fetch orgs (platform admin only) --------
  useEffect(() => {
    if (!user?.is_platform_admin) return;
    apiGet<{ organizations: Organization[] }>("/v1/organizations")
      .then((r) => {
        const m: Record<string, Organization> = {};
        for (const o of r.organizations) m[o.id] = o;
        setOrgsMap(m);
      })
      .catch(() => setOrgsMap({}));
  }, [user?.is_platform_admin, govScope]);

  // -------- Fetch services catalog (for the filter list) --------
  useEffect(() => {
    apiGet<{ services: Service[] }>("/v1/services")
      .then((r) => setAllServices(r.services))
      .catch(console.error);
  }, [govScope]);

  // Generación de página: se incrementa cada vez que la lista se reemplaza
  // desde cero. Un loadMore en vuelo cuya generación quedó vieja descarta su
  // respuesta — evita appendear filas de un filtro anterior.
  const pageGenRef = useRef(0);

  // Primera página: reemplaza la lista completa y resetea el cursor.
  const applyFirstPage = useCallback((r: TraceListResponse) => {
    pageGenRef.current += 1;
    setTraces(r.traces);
    setNextCursor(r.next_cursor);
    setTotal(r.total);
  }, []);

  // -------- Fetch traces whenever filters change --------
  useEffect(() => {
    apiGet<TraceListResponse>(`/v1/traces?${apiParams}`)
      .then(applyFirstPage)
      .catch(console.error);
  }, [apiParams, govScope, applyFirstPage]);

  // -------- Re-fetch when org changes via OrgSelector --------
  const apiParamsRefForOrg = useRef(apiParams);
  apiParamsRefForOrg.current = apiParams;
  const isPlatformAdminRef = useRef(user?.is_platform_admin ?? false);
  isPlatformAdminRef.current = user?.is_platform_admin ?? false;
  useEffect(() => {
    function onOrgChanged() {
      apiGet<{ services: Service[] }>("/v1/services")
        .then((r) => setAllServices(r.services))
        .catch(console.error);
      apiGet<TraceListResponse>(`/v1/traces?${apiParamsRefForOrg.current}`)
        .then(applyFirstPage)
        .catch(console.error);
      if (isPlatformAdminRef.current) {
        apiGet<{ organizations: Organization[] }>("/v1/organizations")
          .then((r) => {
            const m: Record<string, Organization> = {};
            for (const o of r.organizations) m[o.id] = o;
            setOrgsMap(m);
          })
          .catch(() => setOrgsMap({}));
      }
    }
    window.addEventListener("sovra:org-changed", onOrgChanged);
    return () => window.removeEventListener("sovra:org-changed", onOrgChanged);
  }, [applyFirstPage]);

  // Bucket size derived from the active window
  const bucket = useMemo(() => {
    if (!timeWindow.from || !timeWindow.to) return null;
    const sec = (new Date(timeWindow.to).getTime() - new Date(timeWindow.from).getTime()) / 1000;
    return pickBucket(Math.max(1, sec));
  }, [timeWindow]);

  // -------- Fetch histogram across the full window --------
  useEffect(() => {
    if (!timeWindow.from || !timeWindow.to || !bucket) return;
    const sp = new URLSearchParams();
    sp.set("from", timeWindow.from);
    sp.set("to", timeWindow.to);
    sp.set("bucket", bucket.id);
    apiGet<{ buckets: HistogramBucket[] }>(`/v1/traces/histogram?${sp.toString()}`)
      .then((r) => setHistogram(r.buckets ?? []))
      .catch(() => setHistogram([]));
  }, [timeWindow, bucket, govScope]);

  // -------- WebSocket streaming (re-conecta sólo si cambia la org) --------
  const wsGovId = govScope ?? user?.org_id ?? "__all__";
  const apiParamsRef = useRef(apiParams);
  apiParamsRef.current = apiParams;
  // NUEVO patrón: ya no hacemos auto-refetch. Cada vez que llega un trace por WS
  // incrementamos un contador y mostramos un banner "↑ N nuevos traces" arriba
  // de la tabla. El usuario decide cuándo aplicar los nuevos — así NO se le
  // mueve la tabla mientras lee un trace específico (problema clásico de feeds
  // en tiempo real: ej. Twitter/X, Slack).
  const newEventsCounterRef = useRef(0);
  const [newSinceLastRefresh, setNewSinceLastRefresh] = useState(0);

  // Función expuesta arriba para que el banner pueda dispararla.
  const flushNewEvents = useCallback(() => {
    apiGet<TraceListResponse>(`/v1/traces?${apiParamsRef.current}`)
      .then((r) => {
        applyFirstPage(r);
        newEventsCounterRef.current = 0;
        setNewSinceLastRefresh(0);
      })
      .catch(() => {});
  }, [applyFirstPage]);

  // -------- Infinite scroll --------
  // Sentinel al fondo del contenedor scrolleable; cuando entra al viewport
  // (con 300px de anticipación) pedimos la página siguiente y la appendeamos.
  // Deduplicamos por correlation_id por si un trace nuevo corrió los offsets.
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return;
    const gen = pageGenRef.current;
    setLoadingMore(true);
    apiGet<TraceListResponse>(
      `/v1/traces?${apiParamsRef.current}&cursor=${encodeURIComponent(nextCursor)}`
    )
      .then((r) => {
        if (gen !== pageGenRef.current) return; // la lista se reseteó mientras cargábamos
        setTraces((prev) => {
          const seen = new Set(prev.map((t) => t.correlation_id));
          return [...prev, ...r.traces.filter((t) => !seen.has(t.correlation_id))];
        });
        setNextCursor(r.next_cursor);
        setTotal(r.total);
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [nextCursor, loadingMore]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    // root: viewport — en este layout scrollea la PÁGINA (traces-shell crece
    // con el contenido), no el div interno de la lista. Recreamos el observer
    // con cada página nueva: el callback inicial de observe() re-chequea la
    // visibilidad, así que si el sentinel sigue en pantalla (viewport alto)
    // encadena la carga siguiente sin esperar un evento de scroll.
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current();
      },
      { rootMargin: "300px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [nextCursor]);

  // -------- Navegación por teclado (j/k mueven el foco, ↵ abre, esc cierra) --------
  const [focusIdx, setFocusIdx] = useState(-1);
  const tracesRef = useRef(traces);
  tracesRef.current = traces;
  const focusIdxRef = useRef(focusIdx);
  focusIdxRef.current = focusIdx;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)
      ) {
        return;
      }
      if (e.key === "j" || e.key === "k") {
        e.preventDefault();
        const len = tracesRef.current.length;
        if (len === 0) return;
        setFocusIdx((prev) =>
          prev < 0 ? 0 : e.key === "j" ? Math.min(prev + 1, len - 1) : Math.max(prev - 1, 0)
        );
      } else if (e.key === "Enter") {
        const t = tracesRef.current[focusIdxRef.current];
        if (t) setSelectedCid(t.correlation_id);
      } else if (e.key === "Escape") {
        setSelectedCid(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Mantener visible la fila enfocada y resetear el foco cuando cambia el filtro.
  useEffect(() => {
    if (focusIdx < 0) return;
    listScrollRef.current
      ?.querySelector(`[data-idx="${focusIdx}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [focusIdx]);
  useEffect(() => setFocusIdx(-1), [apiParams]);

  useEffect(() => {
    let socket: Socket | null = null;
    let channel: Channel | null = null;

    function onNewTrace() {
      newEventsCounterRef.current += 1;
      setNewSinceLastRefresh(newEventsCounterRef.current);
    }

    (async () => {
      try {
        socket = await connectSocket();
        channel = joinTracesStream(socket, wsGovId, {});
        channel.join().receive("ok", () => setWsConnected(true)).receive("error", () => setWsConnected(false));
        channel.on("trace.created", onNewTrace);
      } catch {
        setWsConnected(false);
      }
    })();

    return () => {
      channel?.leave();
      socket?.disconnect();
    };
  }, [wsGovId]);

  // -------- URL mutation helpers --------
  const update = useCallback(
    (mutator: (sp: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutator(next);
      // Clean default values
      if ((next.get("state") ?? "") === STATE_OPTIONS.map((s) => s.value).join(",")) {
        next.delete("state");
      }
      if (!next.get("service_ids")) next.delete("service_ids");
      if (next.get("range") === "1h") next.delete("range");
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  // -------- Búsqueda libre (q) — draft local con debounce hacia la URL --------
  const [qDraft, setQDraft] = useState(qUrl);
  useEffect(() => setQDraft(qUrl), [qUrl]);
  useEffect(() => {
    if (qDraft === qUrl) return;
    const timer = setTimeout(() => {
      update((sp) => {
        if (qDraft) sp.set("q", qDraft);
        else sp.delete("q");
      });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);

  function setRange(r: RangeId) {
    update((sp) => {
      if (r === "1h") sp.delete("range");
      else sp.set("range", r);
      if (r !== "custom") {
        sp.delete("from");
        sp.delete("to");
      }
    });
    setShowCustom(r === "custom");
  }

  function setCustomFrom(v: string) {
    update((sp) => {
      sp.set("range", "custom");
      if (v) sp.set("from", new Date(v).toISOString());
      else sp.delete("from");
    });
  }
  function setCustomTo(v: string) {
    update((sp) => {
      sp.set("range", "custom");
      if (v) sp.set("to", new Date(v).toISOString());
      else sp.delete("to");
    });
  }

  // Extiende la ventana hacia atrás `seconds`, manteniendo el `to` actual.
  // Convierte el range a "custom" si no lo estaba todavía.
  function extendBackBy(seconds: number) {
    const fromIso = timeWindow.from;
    const toIso = timeWindow.to;
    if (!fromIso || !toIso) return;
    const newFrom = new Date(new Date(fromIso).getTime() - seconds * 1000);
    update((sp) => {
      sp.set("range", "custom");
      sp.set("from", newFrom.toISOString());
      sp.set("to", toIso);
    });
    setShowCustom(true);
  }

  // Setea una ventana absoluta (modo custom).
  function setExplicitWindow(fromIso: string | null, toIso: string | null) {
    update((sp) => {
      sp.set("range", "custom");
      if (fromIso) sp.set("from", fromIso); else sp.delete("from");
      if (toIso) sp.set("to", toIso); else sp.delete("to");
    });
    setShowCustom(true);
  }

  function toggleState(value: string) {
    update((sp) => {
      const cur = new Set((sp.get("state") || STATE_OPTIONS.map((s) => s.value).join(",")).split(","));
      if (cur.has(value)) cur.delete(value);
      else cur.add(value);
      sp.set("state", [...cur].join(","));
    });
  }

  function toggleService(value: string) {
    update((sp) => {
      const cur = new Set((sp.get("service_ids") || "").split(",").filter(Boolean));
      if (cur.has(value)) cur.delete(value);
      else cur.add(value);
      if (cur.size === 0) sp.delete("service_ids");
      else sp.set("service_ids", [...cur].join(","));
    });
  }

  function clearAll() {
    update((sp) => {
      sp.delete("state");
      sp.delete("service_ids");
      sp.delete("range");
      sp.delete("from");
      sp.delete("to");
      sp.delete("q");
    });
    setShowCustom(false);
  }

  function openTrace(t: Trace) {
    // Abrimos el panel SINCRÓNICAMENTE — RTK Query trae los datos por detrás
    // y el panel muestra skeleton hasta que llegan.
    setSelectedCid(t.correlation_id);
  }

  // Mapping uuid → slug a partir del catálogo. Sirve para resolver el
  // `service_ids` (uuids) que vienen en las trazas al slug legible que mostramos.
  const uuidToSlug = useMemo(() => {
    const m = new Map<string, string>();
    allServices.forEach((s) => m.set(s.id, s.slug));
    return m;
  }, [allServices]);

  // Vista global (platform admin sin org): el catálogo trae servicios de más
  // de una org y el slug deja de identificar un servicio — sandbox y prod
  // comparten slug. En ese caso el filtro opera por uuid y cada entrada
  // muestra la org al lado. En vista de org seguimos con slugs (URLs legibles).
  const isMultiOrgCatalog = useMemo(
    () => new Set(allServices.map((s) => s.org_id ?? "")).size > 1,
    [allServices]
  );

  // Service counts seen in current traces, agrupados por la misma clave que
  // usa el filtro (uuid en vista global, slug en vista de org).
  const serviceCounts = useMemo(() => {
    const m = new Map<string, number>();
    traces.forEach((t) =>
      t.service_ids.forEach((id) => {
        const key = isMultiOrgCatalog ? id : (uuidToSlug.get(id) ?? id);
        m.set(key, (m.get(key) ?? 0) + 1);
      }),
    );
    return m;
  }, [traces, uuidToSlug, isMultiOrgCatalog]);

  // Entradas del filter sidebar: catálogo unido a los valores ya activos en
  // la URL (para que un filtro compartido por link nunca "desaparezca").
  const servicesList = useMemo<ServiceFilterEntry[]>(() => {
    if (isMultiOrgCatalog) {
      const entries: ServiceFilterEntry[] = allServices.map((s) => ({
        value: s.id,
        label: s.slug,
        orgName: s.org_id ? (orgsMap[s.org_id]?.name ?? s.org_id.slice(0, 8)) : undefined,
      }));
      const known = new Set(entries.map((e) => e.value));
      activeServices.forEach((v) => {
        if (!known.has(v)) entries.push({ value: v, label: v.slice(0, 8) });
      });
      return entries.sort(
        (a, b) => a.label.localeCompare(b.label) || (a.orgName ?? "").localeCompare(b.orgName ?? "")
      );
    }
    const slugs = new Set(allServices.map((s) => s.slug));
    activeServices.forEach((s) => slugs.add(s));
    return [...slugs].sort().map((slug) => ({ value: slug, label: slug }));
  }, [allServices, activeServices, isMultiOrgCatalog, orgsMap]);

  const counts = useMemo(() => {
    if (histogram.length > 0) {
      return histogram.reduce(
        (acc, b) => ({ ok: acc.ok + b.ok, slow: acc.slow + b.slow, err: acc.err + b.err }),
        { ok: 0, slow: 0, err: 0 }
      );
    }
    let ok = 0, slow = 0, err = 0;
    traces.forEach((t) => {
      if (t.state === "ok") ok++;
      else if (t.state === "slow" || t.state === "in_progress") slow++;
      else err++;
    });
    return { ok, slow, err };
  }, [histogram, traces]);

  const activeFilterCount =
    (activeStates.size < STATE_OPTIONS.length ? STATE_OPTIONS.length - activeStates.size : 0) +
    activeServices.size +
    (range !== "1h" ? 1 : 0) +
    (qUrl ? 1 : 0);

  return (
    <div
      className={`traces-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
      style={{ "--filters-w": `${filtersW}px` } as React.CSSProperties}
    >
      {/* Floating toggle when collapsed */}
      {sidebarCollapsed && (
        <button
          type="button"
          className="filters-rail"
          onClick={toggleSidebar}
          title="Mostrar filtros"
        >
          <Icons.Filter size={14} />
          {activeFilterCount > 0 && <span className="rail-badge">{activeFilterCount}</span>}
        </button>
      )}

      {/* ============ Filters sidebar ============ */}
      {!sidebarCollapsed && (
      <aside className="filters-side">
        {/* Borde arrastrable para ajustar el ancho del panel. Pointer capture
            mantiene el drag aunque el mouse salga del handle. */}
        <div
          className="filters-resize"
          title="Arrastrar para ajustar el ancho"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            resizeRef.current = { startX: e.clientX, startW: filtersW };
          }}
          onPointerMove={(e) => {
            if (!resizeRef.current) return;
            setFiltersW(clampFiltersW(resizeRef.current.startW + (e.clientX - resizeRef.current.startX)));
          }}
          onPointerUp={(e) => {
            if (!resizeRef.current) return;
            const w = clampFiltersW(resizeRef.current.startW + (e.clientX - resizeRef.current.startX));
            resizeRef.current = null;
            e.currentTarget.releasePointerCapture(e.pointerId);
            setFiltersW(w);
            localStorage.setItem("traces_filters_w", String(w));
          }}
        />
        <div className="filters-head">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <Icons.Filter size={13} />
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13.5, letterSpacing: "-0.01em" }}>
              Filtros
            </span>
            {activeFilterCount > 0 && (
              <span
                style={{
                  fontSize: 10.5,
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: "var(--accent)",
                  color: "#fff",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 700,
                  marginLeft: 2,
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {activeFilterCount > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={clearAll}>
                Limpiar
              </button>
            )}
            <button
              type="button"
              className="btn btn-icon btn-ghost"
              onClick={toggleSidebar}
              title="Ocultar filtros"
              style={{ width: 26, height: 26 }}
            >
              <Icons.PanelLeft size={14} />
            </button>
          </div>
        </div>

        <div className="filters-body">
          {/* ---- TIME RANGE ---- */}
          <div className="fsec">
            <div className="fsec-label">
              <span>
                <Icons.Clock size={11} sw={1.7} />
                Período
              </span>
            </div>
            <div className="time-segments">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={range === r.id ? "active" : ""}
                  onClick={() => setRange(r.id)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`custom-toggle ${range === "custom" ? "active" : ""}`}
              onClick={() => {
                if (range === "custom") {
                  setShowCustom((v) => !v);
                } else {
                  setRange("custom");
                  setShowCustom(true);
                }
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Icons.Clock size={11} sw={1.7} />
                Personalizado
              </span>
              <Icons.ChevDown size={11} style={{ transform: showCustom ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
            </button>
            {range === "custom" && showCustom && (
              <div className="custom-range">
                <label>
                  Desde
                  <input
                    type="datetime-local"
                    value={isoToLocal(fromUrl)}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </label>
                <label>
                  Hasta
                  <input
                    type="datetime-local"
                    value={isoToLocal(toUrl)}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </label>
              </div>
            )}
          </div>

          {/* ---- STATE ---- */}
          <div className="fsec">
            <div className="fsec-label">
              <span>Estado</span>
              <span className="fsec-count">{activeStates.size}/{STATE_OPTIONS.length}</span>
            </div>
            <div className="state-grid">
              {STATE_OPTIONS.map((s) => {
                const active = activeStates.has(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => toggleState(s.value)}
                    className={`pill ${active ? "active" : ""}`}
                    style={{ "--pill-color": s.color } as React.CSSProperties}
                  >
                    <span className="dot" />
                    {s.label}
                    {active && <Icons.Check size={11} sw={3} style={{ marginLeft: "auto", opacity: 0.7 }} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ---- SERVICES ---- */}
          <div className="fsec fsec-grow">
            <div className="fsec-label">
              <span>Servicios</span>
              <span className="fsec-count">
                {activeServices.size > 0 ? `${activeServices.size} seleccionados` : `${servicesList.length} totales`}
              </span>
            </div>
            <div className="svc-list">
              {servicesList.map((svc) => {
                const checked = activeServices.has(svc.value);
                const count = serviceCounts.get(svc.value) ?? 0;
                return (
                  <button
                    key={svc.value}
                    type="button"
                    onClick={() => toggleService(svc.value)}
                    className={`svc-item ${checked ? "checked" : ""}`}
                    title={svc.orgName ? `${svc.label} · ${svc.orgName}` : svc.label}
                  >
                    <span className="cbox">{checked && <Icons.Check size={10} sw={3} />}</span>
                    <span className="svc-name">{svc.label}</span>
                    {svc.orgName && <span className="svc-org">{svc.orgName}</span>}
                    {count > 0 && <span className="svc-num">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </aside>
      )}

      {/* ============ Traces table ============ */}
      <section className="traces-table-wrap">
        <div className="toolbar">
          <h1>Traces</h1>
          <span className="meta">
            {total > traces.length ? `${traces.length} de ${total}` : traces.length} resultados
            {newSinceLastRefresh > 0 && (
              <span style={{ marginLeft: 6, color: "var(--accent)", fontWeight: 600 }}>
                · +{newSinceLastRefresh} en cola
              </span>
            )}
            <span className="text-muted" style={{ marginLeft: 8 }}>
              · {wsConnected ? "streaming" : "sin streaming"}
            </span>
          </span>
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <Icons.Search
              size={13}
              style={{ position: "absolute", left: 9, color: "var(--fg-4)", pointerEvents: "none" }}
            />
            <input
              className="input"
              style={{ width: 280, paddingLeft: 28, height: 30, fontSize: 12 }}
              placeholder="Buscar por trace id, email o tag…"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--fg-3)", marginLeft: 10 }}>
            <kbd className="kbd">j</kbd>
            <kbd className="kbd">k</kbd>
            <span>navegar</span>
            <span style={{ color: "var(--fg-4)" }}>·</span>
            <kbd className="kbd">↵</kbd>
            <span>abrir</span>
          </div>
        </div>

        <TimeRangeBar
          range={range}
          fromIso={timeWindow.from}
          toIso={timeWindow.to}
          onExtendBack={extendBackBy}
          onSetWindow={setExplicitWindow}
        />

        <div className="histogram-wrap">
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10.5, color: "var(--fg-3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Distribución {rangeLabel(range)}
            </div>
            <div style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--fg-3)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--st-ok)" }} /> {counts.ok} ok
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--st-degraded)" }} /> {counts.slow} lentas
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--st-major)" }} /> {counts.err} errores
              </span>
            </div>
          </div>
          <MiniHistogram
            buckets={histogram}
            fromIso={timeWindow.from}
            toIso={timeWindow.to}
            bucketMs={bucket?.ms ?? 60_000}
          />
        </div>

        {/* Banner flotante de nuevos traces vía WebSocket — patrón twitter/X:
            cuando llegan eventos nuevos NO movemos la tabla bajo el usuario,
            sino que mostramos un chip con el contador y dejamos que el user
            decida cuándo aplicar. Si la pestaña no tiene foco, igual se
            acumula sin spamear renders. */}
        {newSinceLastRefresh > 0 && (
          <NewTracesBanner count={newSinceLastRefresh} onClick={flushNewEvents} />
        )}

        <div className="traces-grid">
          <div>Hora</div>
          <div>Method</div>
          <div>Status</div>
          <div>Endpoint · Tags</div>
          <div>Servicios</div>
          <div>Latencia</div>
          <div style={{ textAlign: "right" }}>Estado</div>
        </div>

        <div ref={listScrollRef} style={{ flex: 1, overflowY: "auto" }}>
          {traces.length === 0 && (
            <div className="empty">
              <div className="ico"><Icons.Activity size={20} /></div>
              <div>Sin traces con estos filtros.</div>
              {activeFilterCount > 0 && (
                <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={clearAll}>
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
          {traces.map((t, i) => (
            <TraceRow
              key={t.correlation_id}
              t={t}
              idx={i}
              selected={selectedCid === t.correlation_id}
              focused={focusIdx === i}
              onClick={() => openTrace(t)}
              orgName={
                user?.is_platform_admin && getSelectedOrgId() == null && t.org_id
                  ? (orgsMap[t.org_id]?.name ?? t.org_id.slice(0, 8))
                  : undefined
              }
            />
          ))}
          {/* Sentinel del infinite scroll — siempre presente para que el
              IntersectionObserver (creado una sola vez) lo pueda observar. */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <div style={{ padding: "14px 18px", textAlign: "center", fontSize: 12, color: "var(--fg-3)" }}>
              Cargando más traces…
            </div>
          )}
          {!nextCursor && !loadingMore && traces.length > 0 && total > 50 && (
            <div style={{ padding: "14px 18px", textAlign: "center", fontSize: 11.5, color: "var(--fg-4)" }}>
              — fin: {total} traces en el rango —
            </div>
          )}
        </div>
      </section>

      {selectedCid && (
        <DetailPanel correlationId={selectedCid} onClose={() => setSelectedCid(null)} />
      )}
    </div>
  );
}

// ---------- Helpers ----------

function isoToLocal(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function rangeLabel(r: string): string {
  const found = RANGES.find((x) => x.id === r);
  if (found) return `últimos ${found.label}`;
  if (r === "custom") return "rango personalizado";
  return "última hora";
}

const EXTEND_PRESETS = [
  { seconds: 60,         label: "1 min" },
  { seconds: 5 * 60,     label: "5 min" },
  { seconds: 15 * 60,    label: "15 min" },
  { seconds: 60 * 60,    label: "1 h" },
  { seconds: 6 * 60 * 60,label: "6 h" },
  { seconds: 24 * 60 * 60, label: "24 h" },
] as const;

function TimeRangeBar({
  range,
  fromIso,
  toIso,
  onExtendBack,
  onSetWindow,
}: {
  range: RangeId;
  fromIso: string | null;
  toIso: string | null;
  onExtendBack: (seconds: number) => void;
  onSetWindow: (from: string | null, to: string | null) => void;
}) {
  const [extendMenuOpen, setExtendMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [extendSeconds, setExtendSeconds] = useState<number>(15 * 60);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; }
  }, []);

  useEffect(() => {
    setDraftFrom(isoToLocal(fromIso ?? ""));
    setDraftTo(isoToLocal(toIso ?? ""));
  }, [fromIso, toIso, editOpen]);

  if (!fromIso || !toIso) return null;

  const from = new Date(fromIso);
  const to = new Date(toIso);
  const totalSec = Math.max(1, Math.round((to.getTime() - from.getTime()) / 1000));
  const friendlyDuration = humanizeDuration(totalSec);
  const rangeWord = range === "custom" ? "el rango seleccionado" : `los últimos ${friendlyDuration}`;
  const extendLabel = EXTEND_PRESETS.find((p) => p.seconds === extendSeconds)?.label ?? "15 min";

  return (
    <div className="time-bar">
      <div className="time-bar-info">
        <Icons.Info size={13} sw={1.7} />
        <span>
          Mostrando traces <strong>iniciados</strong> en {rangeWord} ·{" "}
          <span className="mono">{formatStamp(from)}</span> →{" "}
          <span className="mono">{formatStamp(to)}</span>
          {tz && <span className="time-bar-tz"> · {tz}</span>}
        </span>
      </div>

      <div className="time-bar-actions">
        <div className="time-bar-extend">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => onExtendBack(extendSeconds)}
            title={`Restar ${extendLabel} al inicio del rango`}
          >
            <Icons.Plus size={11} sw={2.2} />
            Ampliar: {extendLabel}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary time-bar-extend-chev"
            aria-label="Elegir cuánto ampliar"
            onClick={() => setExtendMenuOpen((v) => !v)}
          >
            <Icons.ChevDown size={12} />
          </button>
          {extendMenuOpen && (
            <div className="time-bar-menu" role="listbox">
              {EXTEND_PRESETS.map((p) => (
                <button
                  key={p.seconds}
                  type="button"
                  className={p.seconds === extendSeconds ? "active" : ""}
                  onClick={() => {
                    setExtendSeconds(p.seconds);
                    setExtendMenuOpen(false);
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={() => setEditOpen((v) => !v)}
        >
          <Icons.Clock size={11} sw={1.7} />
          Editar rango
        </button>
      </div>

      {editOpen && (
        <div className="time-bar-edit">
          <label>
            Desde
            <input
              type="datetime-local"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
            />
          </label>
          <label>
            Hasta
            <input
              type="datetime-local"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
            />
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => {
                setDraftFrom(isoToLocal(fromIso));
                setDraftTo(isoToLocal(toIso));
                setEditOpen(false);
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                onSetWindow(
                  draftFrom ? new Date(draftFrom).toISOString() : null,
                  draftTo ? new Date(draftTo).toISOString() : null,
                );
                setEditOpen(false);
              }}
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatStamp(d: Date): string {
  return d.toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function humanizeDuration(totalSec: number): string {
  if (totalSec < 60) return `${totalSec} s`;
  if (totalSec < 3600) return `${Math.round(totalSec / 60)} min`;
  if (totalSec <= 86400) return `${Math.round(totalSec / 3600)} h`;
  const d = totalSec / 86400;
  return Number.isInteger(d) ? `${d} d` : `${d.toFixed(1)} d`;
}

function TraceRow({
  t,
  idx,
  selected,
  focused,
  onClick,
  orgName,
}: {
  t: Trace;
  idx: number;
  selected: boolean;
  focused: boolean;
  onClick: () => void;
  orgName?: string;
}) {
  const slugMap = useServiceSlugMap();
  const stateClass =
    t.state === "ok" ? "ok" :
    t.state === "error" || t.state === "timeout" ? "major" :
    "degraded";

  // Hora siempre visible. Encima, una etiqueta de día que SIEMPRE se muestra
  // pero cambia de forma:
  //   hoy       → "hoy"      (gris claro)
  //   ayer      → "ayer"
  //   esta sem  → "lun" "mar" "mié"... (día corto)
  //   más viejo → "5 jun"
  // Así el operador puede saber a qué día pertenece cada row sin importar
  // el filtro de tiempo activo.
  const traceDate = new Date(t.started_at);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfTrace = new Date(traceDate.getFullYear(), traceDate.getMonth(), traceDate.getDate());
  const daysAgo = Math.round((startOfToday.getTime() - startOfTrace.getTime()) / 86_400_000);
  const time = traceDate.toLocaleTimeString("es-AR", { hour12: false });
  let dayLabel: string;
  if (daysAgo === 0) dayLabel = "hoy";
  else if (daysAgo === 1) dayLabel = "ayer";
  else if (daysAgo >= 2 && daysAgo <= 6)
    dayLabel = traceDate.toLocaleDateString("es-AR", { weekday: "short" });
  else dayLabel = traceDate.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });

  // Datos derivados de los tags que el cliente Sovra Log incrusta. El backend los
  // mergea por trace (deduplica seq=1 + seq=2), así que normalmente acá tenemos
  // method, path, host, status_code y status_class.
  const tags = t.tags ?? {};
  const method = String(tags.http_method ?? tags.method ?? "");
  const path = String(tags.http_path ?? "");
  const host = String(tags.target_host ?? "");
  const statusCode = tags.status_code ? Number(tags.status_code) : null;
  const operation = String(tags.operation ?? "");
  const folio = String(tags.folio ?? "");

  // Tags adicionales que NO son del eje HTTP — los mostramos abajo como mini-chips.
  // Filtramos los técnicos que ya están representados visualmente en otras columnas.
  const TECHNICAL_TAGS = new Set([
    "http_method", "method", "http_path", "target_host", "target_scheme",
    "status_code", "status_class", "outcome", "content_type",
    "instance", "runtime", "pid", "sdk_version", "environment",
  ]);
  const allBusinessTags = Object.entries(tags).filter(([k]) => !TECHNICAL_TAGS.has(k));
  // Mostramos hasta 3 en la fila; el resto queda detrás de "+N" con tooltip
  // listando lo oculto, y click abre el panel lateral con TODOS los tags.
  const businessTags = allBusinessTags.slice(0, 3);
  const hiddenBusinessTags = allBusinessTags.slice(3);

  return (
    <div
      className={`trace-row ${selected ? "selected" : ""} ${focused ? "kb-focus" : ""}`}
      data-idx={idx}
      onClick={onClick}
    >
      {/* Hora */}
      <div>
        <div
          style={{
            fontSize: 10,
            color: "var(--fg-4)",
            fontFamily: "var(--font-mono)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: 1,
          }}
        >
          {dayLabel}
        </div>
        <div className="mono">{time}</div>
      </div>

      {/* Method */}
      <div>
        {method ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              fontSize: 11,
              padding: "2px 6px",
              borderRadius: 4,
              background: `color-mix(in oklab, ${methodColor(method)} 16%, transparent)`,
              color: methodColor(method),
            }}
          >
            {method.toUpperCase()}
          </span>
        ) : (
          <span style={{ color: "var(--fg-4)", fontSize: 11 }}>—</span>
        )}
      </div>

      {/* Status code */}
      <div>
        {statusCode != null ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              fontSize: 11,
              padding: "2px 6px",
              borderRadius: 4,
              background: `color-mix(in oklab, ${statusColor(statusCode)} 14%, transparent)`,
              color: statusColor(statusCode),
            }}
          >
            {statusCode}
          </span>
        ) : (
          <span style={{ color: "var(--fg-4)", fontSize: 11 }}>—</span>
        )}
      </div>

      {/* Endpoint + tags de negocio. Toda la celda usa minWidth:0 + overflow
          hidden por hijo para que jamás empuje el ancho de la tabla. Lo que
          se trunca queda accesible vía tooltip y, sobre todo, vía click en la
          fila (abre el DetailPanel con el contenido completo). */}
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 3, overflow: "hidden" }}>
        {(host || path) && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-1)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={`${host}${path}`}
          >
            {host && <span style={{ color: "var(--fg-3)" }}>{host}</span>}
            {path && <span>{path}</span>}
          </div>
        )}
        {(t.end_user_email || operation || folio || orgName) && (
          <div
            style={{
              display: "flex",
              gap: 5,
              flexWrap: "nowrap",
              alignItems: "center",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {operation && (
              <span
                title={operation}
                style={{
                  fontSize: 10.5,
                  color: "var(--fg-3)",
                  fontFamily: "var(--font-mono)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                {operation}
              </span>
            )}
            {folio && (
              <span
                title={`folio: ${folio}`}
                style={{
                  fontSize: 10,
                  fontFamily: "var(--font-mono)",
                  padding: "1px 5px",
                  borderRadius: 3,
                  background: "var(--bg-2)",
                  color: "var(--fg-2)",
                  flexShrink: 0,
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {folio}
              </span>
            )}
            {t.end_user_email && (
              <span
                title={t.end_user_email}
                style={{
                  fontSize: 10.5,
                  color: "var(--fg-3)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                · {t.end_user_email}
              </span>
            )}
            {orgName && (
              <span
                title={`org: ${orgName}`}
                style={{
                  fontSize: 10,
                  fontFamily: "var(--font-mono)",
                  padding: "1px 5px",
                  borderRadius: 3,
                  background: "var(--bg-2)",
                  border: "1px solid var(--border-1)",
                  color: "var(--fg-3)",
                  flexShrink: 0,
                  maxWidth: 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {orgName}
              </span>
            )}
          </div>
        )}
        {businessTags.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "nowrap",
              minWidth: 0,
              overflow: "hidden",
              alignItems: "center",
            }}
          >
            {businessTags.map(([k, v]) => {
              const value = String(v);
              return (
                <span
                  key={k}
                  title={`${k}=${value}`}
                  style={{
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    color: "var(--fg-3)",
                    display: "inline-flex",
                    alignItems: "center",
                    minWidth: 0,
                    overflow: "hidden",
                    flexShrink: 1,
                  }}
                >
                  <span style={{ color: "var(--fg-4)", flexShrink: 0 }}>{k}</span>
                  <span style={{ flexShrink: 0 }}>=</span>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 140,
                    }}
                  >
                    {value}
                  </span>
                </span>
              );
            })}
            {hiddenBusinessTags.length > 0 && (
              <span
                // El tooltip lista los tags ocultos sin que se pierda info; click
                // en la fila abre el DetailPanel con el set completo y formateado.
                title={hiddenBusinessTags.map(([k, v]) => `${k}=${String(v)}`).join("\n")}
                style={{
                  fontSize: 10,
                  fontFamily: "var(--font-mono)",
                  color: "var(--fg-2)",
                  padding: "0 5px",
                  borderRadius: 3,
                  background: "var(--bg-2)",
                  border: "1px solid var(--border-1)",
                  flexShrink: 0,
                  cursor: "help",
                }}
              >
                +{hiddenBusinessTags.length}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Servicios. Los chips truncan a 130px; lo que excede se ve en hover.
          Si hay más de 2, el "+N" hace tooltip con los nombres restantes —
          la info no se pierde y el click de la fila muestra todo en el panel. */}
      <div style={{ display: "flex", gap: 4, flexWrap: "nowrap", minWidth: 0, overflow: "hidden", alignItems: "center" }}>
        {t.service_ids.slice(0, 2).map((s) => {
          const display = slugMap[s] ?? s.slice(0, 8);
          return (
            <span key={s} className="svc-chip" title={s} style={{ minWidth: 0, flexShrink: 1 }}>
              <span className={`dot status-dot ${stateClass}`} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}>
                {display}
              </span>
            </span>
          );
        })}
        {t.service_ids.length > 2 && (
          <span
            className="svc-chip"
            title={t.service_ids.slice(2).map((id) => slugMap[id] ?? id).join("\n")}
            style={{ flexShrink: 0, cursor: "help" }}
          >
            +{t.service_ids.length - 2}
          </span>
        )}
      </div>

      {/* Latencia con barra horizontal coloreada según duración */}
      <div>
        <LatencyMicroBar durationMs={t.duration_ms} />
      </div>

      {/* Estado (badge color) */}
      <div style={{ textAlign: "right" }}>
        <span className={`badge badge-${stateClass}`}>
          <span className="dot" style={{ background: "currentColor" }} />
          {t.state}
        </span>
      </div>
    </div>
  );
}

// Mini-barra horizontal de latencia con color según el tier:
// verde (rápido) → amarillo (medio) → rojo (lento). El umbral es indicativo;
// para algo más sofisticado habría que comparar contra el p95 del servicio.
function LatencyMicroBar({ durationMs }: { durationMs: number | null | undefined }) {
  if (durationMs == null) {
    return <span style={{ color: "var(--fg-4)", fontSize: 11, fontFamily: "var(--font-mono)" }}>—</span>;
  }
  const ms = Number(durationMs);
  const display = ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)} s`;
  // Escala log-scale para que tanto 50ms como 30s se vean razonablemente:
  // 100ms → 25%, 1s → 50%, 10s → 75%, 100s → 100%.
  const pct = Math.max(8, Math.min(100, (Math.log10(Math.max(ms, 1)) / 5) * 100));
  const color = ms < 500 ? "var(--st-ok)" : ms < 2000 ? "var(--st-degraded)" : "var(--st-major)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-1)" }}>{display}</span>
      <div
        style={{
          height: 4,
          width: "100%",
          borderRadius: 999,
          background: "var(--bg-2)",
          overflow: "hidden",
        }}
        aria-hidden
      >
        <div style={{ width: `${pct}%`, height: "100%", background: color }} />
      </div>
    </div>
  );
}

function MiniHistogram({
  buckets,
  fromIso,
  toIso,
  bucketMs,
}: {
  buckets: HistogramBucket[];
  fromIso: string | null;
  toIso: string | null;
  bucketMs: number;
}) {
  if (!fromIso || !toIso) return null;
  const fromMs = new Date(fromIso).getTime();
  const toMs = new Date(toIso).getTime();
  const totalMs = Math.max(1, toMs - fromMs);
  const widthPct = (bucketMs / totalMs) * 100;
  const max = Math.max(1, ...buckets.map((b) => b.ok + b.slow + b.err));

  // Tick positions across the bottom (~6 evenly spaced)
  const tickCount = 6;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const t = fromMs + ((toMs - fromMs) * i) / tickCount;
    return { pct: (i / tickCount) * 100, label: formatTick(new Date(t), totalMs) };
  });

  return (
    <div style={{ position: "relative" }}>
      <svg width="100%" height={56} viewBox="0 0 100 56" preserveAspectRatio="none">
        {/* Baseline placeholder so an empty range still shows the axis */}
        <rect x={0} y={54} width={100} height={2} fill="var(--bg-2)" />
        {buckets.map((b, i) => {
          const startMs = new Date(b.start).getTime();
          const xPct = ((startMs - fromMs) / totalMs) * 100;
          if (xPct < -widthPct || xPct > 100) return null;
          const okH = (b.ok / max) * 50;
          const slowH = (b.slow / max) * 50;
          const errH = (b.err / max) * 50;
          const w = Math.max(0.4, widthPct * 0.85);
          return (
            <g key={i}>
              {errH > 0 && <rect x={xPct} y={56 - errH} width={w} height={errH} fill="var(--st-major)" />}
              {slowH > 0 && <rect x={xPct} y={56 - errH - slowH} width={w} height={slowH} fill="var(--st-degraded)" />}
              {okH > 0 && <rect x={xPct} y={56 - errH - slowH - okH} width={w} height={okH} fill="var(--st-ok)" />}
            </g>
          );
        })}
      </svg>
      <div className="histogram-ticks">
        {ticks.map((t, i) => (
          <span
            key={i}
            style={{
              left: `${t.pct}%`,
              transform: i === 0 ? "translateX(0)" : i === tickCount ? "translateX(-100%)" : "translateX(-50%)",
            }}
          >
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatTick(d: Date, windowMs: number): string {
  if (windowMs <= 24 * 60 * 60 * 1000) {
    return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });
  }
  if (windowMs <= 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString("es-AR", { weekday: "short", hour: "2-digit", hour12: false });
  }
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
}

function DetailPanel({ correlationId, onClose }: { correlationId: string; onClose: () => void }) {
  const slugMap = useServiceSlugMap();
  // RTK Query: trae el detalle por background. Mientras isLoading=true mostramos
  // skeleton. Si el usuario abre un trace que ya estuvo abierto en los últimos 5
  // minutos, `detail` viene del cache → no se ve skeleton, se renderiza al toque.
  const { data: detail, isLoading, error } = useGetTraceDetailQuery(correlationId);

  // Skeleton mientras carga: estructura visual idéntica al panel real para que
  // el layout no salte cuando llegan los datos.
  if (isLoading || !detail) {
    return <DetailPanelSkeleton onClose={onClose} error={error} />;
  }

  return (
    <aside
      style={{
        // Ancho 900px. El detail panel necesita espacio para mostrar headers,
        // body JSON y timing sin overflow horizontal. En pantallas chicas usa
        // hasta 60vw para no asfixiar la tabla — el min absoluto es 560px.
        width: "min(900px, 60vw)",
        minWidth: 560,
        maxWidth: "100vw",
        borderLeft: "1px solid var(--border-1)",
        background: "var(--bg-1)",
        position: "fixed",
        top: 52,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "20px 22px",
        zIndex: 50,
      }}
    >
      {/* Header del panel: una sola fila con título + close anclado a la derecha.
          Usamos min-width:0 en el contenedor de texto para que el flex permita
          truncar / wrappear sin empujar el botón close fuera del panel. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            title={detail.trace.correlation_id}
            style={{
              fontSize: 11,
              color: "var(--fg-4)",
              fontFamily: "var(--font-mono)",
              marginBottom: 4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {detail.trace.correlation_id}
          </div>
          <div
            title={detail.trace.service_ids[0]}
            style={{
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "var(--font-display)",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {detail.trace.service_ids[0]
              ? (slugMap[detail.trace.service_ids[0]] ?? detail.trace.service_ids[0])
              : "Trace"}
          </div>
          {detail.trace.end_user_email && (
            <div
              style={{
                fontSize: 12,
                color: "var(--fg-3)",
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {detail.trace.end_user_email}
            </div>
          )}
          {Object.keys(detail.trace.tags ?? {}).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {Object.entries(detail.trace.tags).map(([k, v]) => (
                <span
                  key={k}
                  title={`${k}=${String(v)}`}
                  style={{
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: "var(--bg-2)",
                    border: "1px solid var(--border-1)",
                    display: "inline-flex",
                    alignItems: "center",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <span style={{ color: "var(--fg-4)", flexShrink: 0 }}>{k}</span>
                  <span style={{ margin: "0 2px", flexShrink: 0 }}>=</span>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 240,
                    }}
                  >
                    {String(v)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          className="btn btn-icon btn-ghost"
          type="button"
          onClick={onClose}
          style={{ flexShrink: 0 }}
        >
          <Icons.Close size={14} />
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <span className={`badge badge-${detail.trace.state === "ok" ? "ok" : detail.trace.state === "error" ? "major" : "degraded"}`}>
          <span className="dot" style={{ background: "currentColor" }} />
          {detail.trace.state}
        </span>
        {detail.trace.duration_ms != null && (
          <span className="badge badge-muted">{(detail.trace.duration_ms / 1000).toFixed(1)} s</span>
        )}
        <span className="badge badge-muted">{detail.events.length} eventos</span>
      </div>

      <div style={{ fontSize: 10.5, color: "var(--fg-3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
        Timeline
      </div>

      <ul style={{ listStyle: "none", padding: 0, display: "grid", gap: 8, minWidth: 0 }}>
        {detail.events.map((ev) => (
          <EventRow key={ev.id} ev={ev} trace_id={detail.trace.correlation_id} />
        ))}
      </ul>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EventRow: vista detallada de un evento (request/response/error).
//
// Decisiones de diseño:
//   • Eager fetch del detalle en useEffect — NO esperar click. La vista de un
//     trace tiene típicamente 2 eventos; cargarlos al instante es más rápido
//     en wall-clock para el usuario que el lazy (no hay click → no hay espera).
//   • Siempre expandido. El toggle anterior era invisible para el usuario y
//     requería un click no obvio.
//   • Render estilo "petición real": method+path en mono, status con color,
//     headers como tabla, body con pretty print, timing como barra apilada.
//     Inspirado en OpenStatus.
//   • Botón "Ver JSON" abre un modal con el JsonViewer crudo si alguien quiere
//     inspeccionar el shape exacto.
// ─────────────────────────────────────────────────────────────────────────────

// Shape de los campos JSON-libres que el backend devuelve por evento. Los tipamos
// acá (en vez de `Record<string, unknown>`) para que el JSX no se queje de
// `unknown` cuando hacemos checks tipo `{timing && <TimingBar ... />}`.
interface EventRequestShape {
  method?: string;
  path?: string;
  query?: string | null;
  headers?: Record<string, string>;
  body_preview?: unknown;
  body_size_bytes?: number | null;
  body_truncated?: boolean;
}
interface EventResponseShape {
  status?: number;
  headers?: Record<string, string>;
  body_preview?: unknown;
  body_size_bytes?: number | null;
  body_truncated?: boolean;
}
interface EventMetadataShape {
  timing?: { ttfb_ms?: number; transfer_ms?: number; total_ms?: number };
  error?: { type?: string; cause?: string; message?: string };
}

function EventRow({ ev }: { ev: TraceEvent; trace_id: string }) {
  const [rawOpen, setRawOpen] = useState(false);

  // El endpoint /v1/traces/:id ya devuelve los events con TODOS los campos
  // (request, response, error_stack, metadata). Antes hacíamos un lazy fetch
  // adicional acá; ya no hace falta y se ahorran 2-3 round-trips por trace.
  const data = ev;
  const isError = data.level === "error" || data.action === "error";
  const isRequest = data.action === "request";
  const accent = isError ? "var(--st-major)" : isRequest ? "var(--accent)" : "var(--st-ok)";

  const request = (data.request as EventRequestShape | null) ?? null;
  const response = (data.response as EventResponseShape | null) ?? null;
  const metadata = (data.metadata as EventMetadataShape | null) ?? null;
  const timing = metadata?.timing ?? null;
  const errorMeta = metadata?.error ?? null;

  return (
    <li
      className="card"
      style={{
        padding: 0,
        overflow: "hidden",
        borderLeft: `3px solid ${accent}`,
        minWidth: 0,
      }}
    >
      {/* Cabecera */}
      <header
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          padding: "10px 14px",
          background: "var(--bg-1)",
          borderBottom: "1px solid var(--border-1)",
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 999,
              fontFamily: "var(--font-mono)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              background: `color-mix(in oklab, ${accent} 15%, transparent)`,
              color: accent,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {isRequest ? "→ request" : isError ? "← error" : "← response"}
          </span>
          <span style={{ color: "var(--fg-4)", fontSize: 11, fontFamily: "var(--font-mono)", flexShrink: 0 }}>
            seq {data.sequence}
          </span>
        </div>
        {data.duration_ms != null && (
          <span style={{ color: "var(--fg-2)", fontSize: 12, fontFamily: "var(--font-mono)", flexShrink: 0 }}>
            {data.duration_ms} ms
          </span>
        )}
        <button
          type="button"
          onClick={() => setRawOpen(true)}
          title="Ver evento como JSON crudo"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "3px 8px",
            background: "var(--bg-2)",
            border: "1px solid var(--border-1)",
            borderRadius: 5,
            color: "var(--fg-2)",
            fontSize: 10.5,
            fontFamily: "var(--font-mono)",
            cursor: "pointer",
            transition: "all 0.12s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-0)";
            (e.currentTarget as HTMLButtonElement).style.color = "var(--fg-1)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-2)";
            (e.currentTarget as HTMLButtonElement).style.color = "var(--fg-2)";
          }}
        >
          <Icons.Cube size={10} />
          <span>{"{ }"} JSON</span>
        </button>
      </header>

      {/* Cuerpo */}
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
        {/* Línea de método + path estilo HTTP */}
        {request && (
          <RequestLine
            method={String(request.method ?? "GET")}
            path={String(request.path ?? "")}
            query={request.query ? String(request.query) : null}
          />
        )}
        {response && (
          <ResponseLine
            status={Number(response.status ?? 0)}
            sizeBytes={response.body_size_bytes as number | null}
          />
        )}

        {/* Timing breakdown como barra */}
        {timing != null && <TimingBar timing={timing} />}

        {/* Headers (request → request.headers; response → response.headers) */}
        {request?.headers && Object.keys(request.headers).length > 0 && (
          <KeyValueTable title="Request headers" entries={request.headers as Record<string, string>} />
        )}
        {response?.headers && Object.keys(response.headers).length > 0 && (
          <KeyValueTable title="Response headers" entries={response.headers as Record<string, string>} />
        )}

        {/* Body preview */}
        {request?.body_preview != null && request.body_preview !== "" && (
          <BodyPreview
            title="Request body"
            value={request.body_preview}
            sizeBytes={request.body_size_bytes as number | null}
            truncated={Boolean(request.body_truncated)}
          />
        )}
        {response?.body_preview != null && response.body_preview !== "" && (
          <BodyPreview
            title="Response body"
            value={response.body_preview}
            sizeBytes={response.body_size_bytes as number | null}
            truncated={Boolean(response.body_truncated)}
          />
        )}

        {/* Mensaje de error destacado */}
        {(errorMeta || data.error_stack) && (
          <div
            style={{
              padding: "10px 12px",
              background: "color-mix(in oklab, var(--st-major) 10%, transparent)",
              border: "1px solid color-mix(in oklab, var(--st-major) 28%, transparent)",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--st-major)",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Error
            </div>
            {errorMeta?.type != null && (
              <div style={{ fontFamily: "var(--font-mono)" }}>
                <strong>{String(errorMeta.type)}</strong>
                {errorMeta.cause ? ` (${String(errorMeta.cause)})` : ""}
              </div>
            )}
            {errorMeta?.message != null && (
              <div style={{ marginTop: 4, fontFamily: "var(--font-mono)" }}>{String(errorMeta.message)}</div>
            )}
            {data.error_stack && (
              <pre
                style={{
                  marginTop: 8,
                  background: "var(--bg-1)",
                  color: "var(--fg-1)",
                  padding: 8,
                  borderRadius: 4,
                  fontSize: 10.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  overflowY: "auto",
                  overflowX: "hidden",
                  maxHeight: 180,
                  fontFamily: "var(--font-mono)",
                  minWidth: 0,
                }}
              >
                {data.error_stack}
              </pre>
            )}
          </div>
        )}
      </div>

      {rawOpen && (
        <RawJsonDialog event={data} onClose={() => setRawOpen(false)} />
      )}
    </li>
  );
}

// ─── Helpers de presentación del evento ─────────────────────────────────────

function methodColor(method: string): string {
  const m = method.toUpperCase();
  if (m === "GET") return "#3b82f6";
  if (m === "POST") return "#22c55e";
  if (m === "PUT" || m === "PATCH") return "#f59e0b";
  if (m === "DELETE") return "#ef4444";
  return "#a3a3a3";
}

function statusColor(status: number): string {
  if (status >= 500) return "var(--st-major)";
  if (status >= 400) return "var(--st-degraded)";
  if (status >= 300) return "#a78bfa";
  if (status >= 200) return "var(--st-ok)";
  return "var(--fg-3)";
}

function RequestLine({ method, path, query }: { method: string; path: string; query: string | null }) {
  const color = methodColor(method);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        minWidth: 0,
      }}
    >
      <span
        style={{
          padding: "3px 8px",
          borderRadius: 4,
          background: `color-mix(in oklab, ${color} 18%, transparent)`,
          color,
          fontWeight: 700,
          fontSize: 11,
          flexShrink: 0,
          lineHeight: 1.4,
        }}
      >
        {method.toUpperCase()}
      </span>
      <span
        style={{
          color: "var(--fg-1)",
          wordBreak: "break-all",
          overflowWrap: "anywhere",
          minWidth: 0,
          flex: 1,
          lineHeight: 1.45,
        }}
      >
        {path}
        {query && <span style={{ color: "var(--fg-3)" }}>{query}</span>}
      </span>
    </div>
  );
}

function ResponseLine({ status, sizeBytes }: { status: number; sizeBytes: number | null }) {
  const color = statusColor(status);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "var(--font-mono)", fontSize: 12 }}>
      <span
        style={{
          padding: "3px 8px",
          borderRadius: 4,
          background: `color-mix(in oklab, ${color} 18%, transparent)`,
          color,
          fontWeight: 700,
          fontSize: 11,
        }}
      >
        {status}
      </span>
      <span style={{ color: "var(--fg-3)" }}>respuesta</span>
      {sizeBytes != null && (
        <span style={{ color: "var(--fg-3)", marginLeft: "auto", fontSize: 11 }}>
          {formatBytes(sizeBytes)}
        </span>
      )}
    </div>
  );
}

function TimingBar({ timing }: { timing: Record<string, number> }) {
  const ttfb = Number(timing.ttfb_ms ?? 0);
  const transfer = Number(timing.transfer_ms ?? 0);
  const total = Number(timing.total_ms ?? ttfb + transfer);
  if (total === 0) return null;
  const ttfbPct = (ttfb / total) * 100;
  const transferPct = (transfer / total) * 100;
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          color: "var(--fg-3)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 6,
        }}
      >
        Timing
      </div>
      <div
        style={{
          display: "flex",
          height: 8,
          borderRadius: 999,
          overflow: "hidden",
          background: "var(--bg-2)",
        }}
      >
        <div style={{ width: `${ttfbPct}%`, background: "#a78bfa" }} title={`TTFB: ${ttfb} ms`} />
        <div style={{ width: `${transferPct}%`, background: "#22c55e" }} title={`Transfer: ${transfer} ms`} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--fg-3)" }}>
        <span>
          <span style={{ color: "#a78bfa" }}>■</span> ttfb {ttfb} ms
        </span>
        <span>
          <span style={{ color: "#22c55e" }}>■</span> transfer {transfer} ms
        </span>
        <span style={{ color: "var(--fg-2)" }}>total {total} ms</span>
      </div>
    </div>
  );
}

function KeyValueTable({ title, entries }: { title: string; entries: Record<string, string> }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 10.5,
          color: "var(--fg-3)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--border-1)",
          borderRadius: 6,
          padding: 4,
          minWidth: 0,
        }}
      >
        {Object.entries(entries).map(([k, v]) => (
          <div
            key={k}
            style={{
              display: "grid",
              // minmax(0,...) es la clave: sin esto los grid items con texto
              // muy largo (tokens, URLs sin espacios) hacen overflow al
              // contenedor padre, rompiendo el ancho del modal.
              gridTemplateColumns: "minmax(0, 150px) minmax(0, 1fr)",
              gap: 12,
              padding: "5px 8px",
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              borderBottom: "1px solid var(--border-1)",
              minWidth: 0,
            }}
          >
            <span
              title={k}
              style={{
                color: "var(--fg-3)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {k}
            </span>
            <span
              style={{
                color: "var(--fg-1)",
                wordBreak: "break-all",
                overflowWrap: "anywhere",
                minWidth: 0,
              }}
            >
              {String(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BodyPreview({
  title,
  value,
  sizeBytes,
  truncated,
}: {
  title: string;
  value: unknown;
  sizeBytes: number | null;
  truncated: boolean;
}) {
  const isObj = value !== null && typeof value === "object";
  const pretty = isObj ? JSON.stringify(value, null, 2) : String(value);
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          fontSize: 10.5,
          color: "var(--fg-3)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 6,
        }}
      >
        <span>{title}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontWeight: 500, textTransform: "none", letterSpacing: 0, flexShrink: 0 }}>
          {sizeBytes != null && formatBytes(sizeBytes)}
          {truncated && <span style={{ marginLeft: 6, color: "var(--st-degraded)" }}>· truncated</span>}
        </span>
      </div>
      <pre
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--border-1)",
          padding: 10,
          borderRadius: 6,
          fontSize: 11,
          fontFamily: "var(--font-mono)",
          // pre-wrap mantiene los saltos del JSON pretty-printed pero permite
          // wrappear líneas larguísimas (ej. URLs en strings) en lugar de
          // forzar scroll horizontal que rompe la sensación del modal.
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          overflowY: "auto",
          overflowX: "hidden",
          maxHeight: 220,
          margin: 0,
          lineHeight: 1.5,
          color: "var(--fg-1)",
          minWidth: 0,
        }}
      >
        {pretty}
      </pre>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function RawJsonDialog({ event, onClose }: { event: TraceEvent; onClose: () => void }) {
  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 720, maxWidth: "94vw", maxHeight: "90vh", padding: 0, display: "flex", flexDirection: "column" }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-1)",
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontFamily: "var(--font-display)" }}>JSON crudo del evento</h3>
            <div style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)", marginTop: 2 }}>
              sequence {event.sequence} · {event.action}
            </div>
          </div>
          <button type="button" className="btn btn-icon btn-ghost" onClick={onClose}>
            <Icons.Close size={14} />
          </button>
        </header>
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          <JsonViewer value={event} />
        </div>
      </div>
    </div>
  );
}

// Antes existía aquí un helper `Field` para etiquetar bloques JSON; lo
// removimos cuando EventRow pasó a una vista estructurada (RequestLine,
// ResponseLine, KeyValueTable, BodyPreview).

// ─────────────────────────────────────────────────────────────────────────────
// NewTracesBanner: chip flotante que indica cuántos traces llegaron por
// WebSocket desde el último refresh. El usuario hace click para aplicarlos.
//
// Decisión de UX: NO auto-aplicamos. Si el operador está leyendo un trace,
// que la tabla cambie bajo sus pies es disruptive. Si quiere ver lo nuevo,
// hace click. El contador queda visible hasta que clickee.
// ─────────────────────────────────────────────────────────────────────────────

function NewTracesBanner({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 4,
        display: "flex",
        justifyContent: "center",
        padding: "8px 0",
        background:
          "linear-gradient(to bottom, var(--bg-0) 0%, var(--bg-0) 75%, transparent 100%)",
        pointerEvents: "none",
      }}
    >
      <button
        type="button"
        onClick={onClick}
        style={{
          pointerEvents: "auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 16px",
          background: "var(--accent)",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          cursor: "pointer",
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: "var(--font-display)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          animation: "sovraNewTraces 0.3s ease-out",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
          (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 6px 20px rgba(0,0,0,0.24)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
          (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 16px rgba(0,0,0,0.18)";
        }}
      >
        <Icons.Activity size={12} sw={2} />
        <span>
          {count} nuevo{count === 1 ? "" : "s"} trace{count === 1 ? "" : "s"} disponible
          {count === 1 ? "" : "s"} — click para ver
        </span>
      </button>
      <style>{`
        @keyframes sovraNewTraces {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DetailPanelSkeleton: mientras RTK Query trae el detalle del trace.
//
// Mismo layout que DetailPanel real (mismo width, mismo padding, mismas
// secciones) para que cuando llegan los datos NO haya layout shift —
// la transición es solo "los rectángulos pulsantes se reemplazan por contenido".
// ─────────────────────────────────────────────────────────────────────────────

function DetailPanelSkeleton({ onClose, error }: { onClose: () => void; error: unknown }) {
  return (
    <aside
      style={{
        width: "min(900px, 60vw)",
        minWidth: 560,
        maxWidth: "100vw",
        borderLeft: "1px solid var(--border-1)",
        background: "var(--bg-1)",
        position: "fixed",
        top: 52,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "20px 22px",
        zIndex: 50,
      }}
    >
      {/* Header con botón close real — para que el usuario pueda cancelar
          mientras espera, no se siente "atrapado". */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <SkelLine width={220} height={11} mb={6} />
          <SkelLine width={180} height={18} mb={6} />
          <SkelLine width={140} height={12} />
        </div>
        <button className="btn btn-icon btn-ghost" type="button" onClick={onClose}>
          <Icons.Close size={14} />
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <SkelLine width={50} height={20} radius={999} />
        <SkelLine width={50} height={20} radius={999} />
        <SkelLine width={70} height={20} radius={999} />
      </div>

      <div style={{ fontSize: 10.5, color: "var(--fg-3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
        Timeline
      </div>

      {/* Bloques que simulan los 2 events típicos de un trace */}
      <ul style={{ listStyle: "none", padding: 0, display: "grid", gap: 8 }}>
        {[0, 1].map((i) => (
          <li
            key={i}
            className="card"
            style={{
              padding: 0,
              overflow: "hidden",
              borderLeft: "3px solid var(--bg-2)",
            }}
          >
            <header
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                padding: "10px 14px",
                background: "var(--bg-1)",
                borderBottom: "1px solid var(--border-1)",
              }}
            >
              <SkelLine width={70} height={16} radius={999} />
              <SkelLine width={50} height={11} />
              <div style={{ flex: 1 }} />
              <SkelLine width={60} height={12} />
            </header>
            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 14 }}>
              <SkelLine width="60%" height={14} />
              <SkelLine width="40%" height={14} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <SkelLine width="100%" height={8} radius={999} />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <SkelLine width={60} height={11} />
                  <SkelLine width={60} height={11} />
                  <SkelLine width={60} height={11} />
                </div>
              </div>
              <div>
                <SkelLine width={120} height={10} mb={6} />
                <SkelLine width="100%" height={80} radius={6} />
              </div>
            </div>
          </li>
        ))}
      </ul>

      {error != null && (
        <div
          style={{
            marginTop: 14,
            padding: "10px 12px",
            background: "color-mix(in oklab, var(--st-major) 10%, transparent)",
            border: "1px solid color-mix(in oklab, var(--st-major) 30%, transparent)",
            borderRadius: 6,
            fontSize: 12,
            color: "var(--st-major)",
          }}
        >
          No se pudo cargar el trace. Probá cerrar y reabrir.
        </div>
      )}

      {/* Keyframes inline — TypeScript no acepta @keyframes en style={{}}, así que
          inyectamos un <style> chico solo para esto. Idempotente: el mismo nombre
          de keyframe definido N veces no causa problemas. */}
      <style>{`
        @keyframes sovraSkelPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
      `}</style>
    </aside>
  );
}

function SkelLine({
  width,
  height,
  radius = 4,
  mb = 0,
}: {
  width: number | string;
  height: number;
  radius?: number;
  mb?: number;
}) {
  return (
    <div
      aria-hidden
      style={{
        width,
        height,
        background: "var(--bg-2)",
        borderRadius: radius,
        marginBottom: mb,
        animation: "sovraSkelPulse 1.4s ease-in-out infinite",
      }}
    />
  );
}
